import { BankStatement, TrackedFileDocument } from "./statement";
import { UnderwritingDecision } from "./underwriting";

export type ProcessingStatus = "pending" | "processing" | "completed" | "failed";

export const PROCESSING_STATUS_COLORS: Record<
  string,
  "default" | "primary" | "success" | "warning" | "error"
> = {
  pending: "default",
  processing: "primary",
  completed: "success",
  failed: "error",
};

export const APP_STATUSES = [
  "submitted",
  "needs_input",
  "ai_analyzing",
  "funder_review",
  "contract_ready",
  "contract_sent",
  "contract_signed",
  // Merchant uploaded a voided check; staff must verify the bank.
  "bank_verification_required",
  "plaid_initiated",
  "plaid_completed",
  "final_underwriting",
  "funded",
  "repayment",
  "defaulted",
  "paid_off",
  "refi_ready",
  "declined",
  "failed",
] as const;
export type AppStatus = (typeof APP_STATUSES)[number];

/** Post-funding / servicing statuses — excluded from the /borrowers list status filter. */
export const POST_FUNDING_APP_STATUSES: AppStatus[] = [
  "repayment",
  "defaulted",
  "paid_off",
  "refi_ready",
];

/** Statuses where the Final Underwriting tab is enabled. */
export const FINAL_UW_TAB_ENABLED_STATUSES: readonly AppStatus[] = [
  "contract_signed",
  "bank_verification_required",
  "plaid_initiated",
  "plaid_completed",
  "final_underwriting",
  "funded",
  "repayment",
  "defaulted",
  "paid_off",
  "refi_ready",
  // Kept reachable after a Final Decision "Decline" so the pencil-edit flow
  // (Fund <-> Decline) can still open this tab to correct a mis-click.
  "declined",
];

/**
 * Statuses the "Force to Final Underwriting" escape hatch is offered from.
 * Mirrors `PLAID_FORCE_FINAL_UW_STATUSES` in backend/apps/borrowers/views.py;
 * keep the two in sync.
 */
export const PLAID_FORCE_FINAL_UW_STATUSES: readonly AppStatus[] = [
  "contract_signed",
  "bank_verification_required",
  "plaid_initiated",
  "plaid_completed",
];

export const APP_STATUS_COLORS: Record<
  AppStatus,
  "default" | "primary" | "success" | "warning" | "error"
> = {
  submitted: "default",
  needs_input: "warning",
  ai_analyzing: "primary",
  funder_review: "primary",
  contract_ready: "primary",
  contract_sent: "primary",
  contract_signed: "primary",
  bank_verification_required: "warning",
  plaid_initiated: "primary",
  plaid_completed: "primary",
  final_underwriting: "warning",
  funded: "success",
  repayment: "warning",
  defaulted: "error",
  paid_off: "success",
  refi_ready: "warning",
  declined: "error",
  failed: "error",
};

export interface AppStatusChoice {
  value: AppStatus;
  label: string;
}
export type AppStatusTransitions = Record<AppStatus, AppStatus[]>;

export type IntakeStatus = "" | "processing" | "ready" | "needs_input" | "processing_error";

/** Legal entity type of the business, extracted from application docs or set manually. */
export const ENTITY_TYPES = ["LLC", "Corp", "LP", "LLP", "SP"] as const;
export type EntityType = (typeof ENTITY_TYPES)[number];
export const DEFAULT_ENTITY_TYPE: EntityType = "LLC";

/** Per-document breakdown behind a statement-related readiness issue: the
 * original (user-recognizable) filename plus whatever month/year the AI
 * extracted, or null when it couldn't be detected. */
export interface IntakeIssueStatement {
  id: number; // TrackedFile id — used to target a manual month/year correction
  filename: string;
  month: string | null; // normalized "01".."12"
  year: string | null; // 4-digit year
}

/** Document listed on no_bank_statements for manual classification. */
export interface IntakeIssueDocument {
  id: number;
  filename: string;
  doc_type: string;
}

/** One structured readiness issue from the intake evaluator. */
export interface IntakeIssue {
  code: string;
  // optional metadata (varies by code)
  longest_run_length?: number;
  distinct_months?: number;
  floor_month?: string;
  newest_month?: string;
  newest_run_month?: string;
  count?: number;
  reason?: string;
  statements?: IntakeIssueStatement[];
  documents?: IntakeIssueDocument[];
  filenames?: string[];
}

export interface Borrower {
  id: number;
  company_name: string;
  owner_name: string;
  email?: string;
  phone?: string;
  /** Owner (personal/home) address */
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  /** Business (merchant/DBA location) address */
  business_address_line1?: string;
  business_city?: string;
  business_state?: string;
  business_postal_code?: string;
  ein?: string;
  entity_type?: EntityType | string;
  application_submission_date?: string;
  processing_status?: ProcessingStatus;
  statement_count?: number;
  latest_decision_status?: string;
  broker_email?: string;
  /** Display name for the broker (name when set; email for ghosts / unnamed). */
  broker_name?: string | null;
  broker_company_name?: string;
  underwriter_email?: string | null;
  /** Display name for the underwriter (name when set; email when unnamed). */
  underwriter_name?: string | null;
  /** Funder organization id stamped on the application. */
  organization?: number;
  organization_name?: string;
  default_history_count?: number;
  is_read?: boolean;
  last_status_changed_at?: string;
  created_at: string;
  updated_at: string;
  app_status: AppStatus;
  broker_is_steddyuser?: boolean;
  intake_status?: IntakeStatus;
  intake_issues?: IntakeIssue[];
  intake_last_evaluated_at?: string | null;
  merchants_dba?: string | null;
  owner_drivers_license?: string | null;
  /** Owner date of birth, YYYY-MM-DD. Optional. */
  owner_date_of_birth?: string | null;
  /** Owner SSN, formatted or digits. Optional. Omitted from list payloads. */
  owner_ssn?: string | null;
  bank_accounts?: MerchantBankAccount[];
  /**
   * Workflow board only: true when the most recent contract submission has
   * been opened by the recipient but not yet signed (mirrors the "delivered"
   * DocuSeal status). Absent on other endpoints.
   */
  contract_viewed?: boolean;
  /**
   * Workflow board only: earliest inbound email subject for this application.
   * Used as the card title when ``company_name`` is still empty.
   */
  email_subject?: string;
}

/** One bank account on file for a merchant (Plaid Auth or manual entry). */
export interface MerchantBankAccount {
  id: number;
  source: "manual" | "plaid";
  is_primary: boolean;
  /** True only when this account's ACH addendum submission is completed. */
  addendum_signed: boolean;
  bank_name: string;
  name_on_account: string;
  routing_number: string;
  account_number: string;
  wire_routing_number: string;
  account_mask: string;
  account_type: string;
  account_subtype: string;
  is_tokenized_account_number: boolean;
  auth_method: string;
  balance_available: string | null;
  balance_current: string | null;
  plaid_item: number | null;
  plaid_account_id: string;
  plaid_fetched_at: string | null;
  created_at: string;
  updated_at: string;
}

/** Row ids served by the deal checklist (`borrowers/deal_facts.py`). */
export type DealChecklistRowId =
  | "disclosure"
  | "main_contract"
  | "bank_connection"
  | "ach_addendum"
  | "id_verification";

/** How a sent flow is waiting on this row. Absent on older payloads. */
export type ChecklistAttention = "done" | "pending" | "missing" | "not_required" | "unasked";

/**
 * One merchant checklist row, computed server-side from `DealFacts`. Each step of
 * a document request is a row of its own (`document_…` id) that names itself in `label`.
 */
export interface DealChecklistRow {
  id: DealChecklistRowId | `document_${string}`;
  visible: boolean;
  required: boolean;
  satisfied: boolean;
  attention?: ChecklistAttention;
  /** Set on requested-document rows. */
  label?: string;
  /** `label` is a name staff typed (a requested "Other" document). */
  custom?: boolean;
}

/** Writable fields for creating/updating a manual bank account. */
export interface MerchantBankAccountInput {
  bank_name?: string;
  name_on_account?: string;
  routing_number?: string;
  account_number?: string;
}

/**
 * Structured `/borrowers/` list params a search string parses into (see
 * utils/searchQueryParser). Arrays are multi-value filters (any of), sent as
 * repeated params; exclude_* drop rows matching any of their values.
 */
export interface SearchFilterParams {
  search?: string;
  app_status?: string | string[];
  owner_name?: string | string[];
  company_name?: string | string[];
  email?: string | string[];
  ein?: string | string[];
  broker_company_name?: string | string[];
  underwriter_email?: string | string[];
  state?: string | string[];
  created_after?: string;
  created_before?: string;
  organization_name?: string | string[];
  exclude_app_status?: string | string[];
  exclude_owner_name?: string | string[];
  exclude_company_name?: string | string[];
  exclude_email?: string | string[];
  exclude_ein?: string | string[];
  exclude_broker_company_name?: string | string[];
  exclude_underwriter_email?: string | string[];
  exclude_state?: string | string[];
  exclude_organization_name?: string | string[];
}

export interface BorrowerFilters extends SearchFilterParams {
  ordering?: string;
  organization_id?: number;
  // Comma-separated app_status values (workflow board); maps to ?app_status__in=
  app_status__in?: string;
  // Lightweight list variant flag (e.g. "workflow")
  view?: string;
}

// ─── Application Notes ───────────────────────────────────────────────────────

export interface ApplicationNoteEntry {
  id: string;
  text: string;
  timestamp: string; // ISO 8601
  /** "automated" for system-generated field diffs, "user" for hand-typed notes.
   *  Absent on notes written before tagging existed. */
  noteType?: "automated" | "user";
  /** Set when the note was typed in the Approve/Decline popup. */
  decisionAction?: "approve" | "decline" | null;
  /** Present on automated notes: {field: [oldRepr, newRepr]}. */
  changes?: Record<string, [string, string]>;
  /** "agent" when an agent wrote the note; absent on web notes. */
  via?: "agent";
}

export interface ApplicationUserNotes {
  userName: string;
  userRole: "admin" | "broker" | "underwriter" | "system";
  notes: ApplicationNoteEntry[];
}

/** Keyed by str(user.id) or "system" */
export type ApplicationNotesDict = Record<string, ApplicationUserNotes>;

/** Flattened shape used by NotesDialog for rendering the sorted thread */
export interface FlatApplicationNote extends ApplicationNoteEntry {
  userId: string;
  userName: string;
  userRole: ApplicationUserNotes["userRole"];
}

// ─────────────────────────────────────────────────────────────────────────────

export interface BorrowerDetail extends Borrower {
  statements: BankStatement[];
  documents: TrackedFileDocument[];
  decisions: UnderwritingDecision[];
  latest_decision?: UnderwritingDecision;
  full_address?: string;
  business_full_address?: string;
  address_validated?: boolean;
  application_notes?: ApplicationNotesDict;
  broker?: {
    id: number;
    email: string;
    first_name?: string;
    last_name?: string;
    name?: string;
    company_name?: string;
    is_steddyuser?: boolean;
  };
  requires_plaid?: boolean;
  plaid_link_last_sent_at?: string | null;
  plaid_pdf_fetch_failed?: boolean;
  /** Presigned or absolute URL when asset report PDF is stored (plaid_completed). */
  plaid_asset_report_pdf_url?: string | null;
  /**
   * pending: bank(s) connected but merchant hasn't submitted yet.
   * processing: asset report requested, waiting on Plaid.
   */
  plaid_report_status?: "pending" | "processing" | "failed" | "ready" | null;
  /** Set only when plaid_report_status === "failed". */
  plaid_report_error?: { code: string; message: string } | null;
  /** Latest asset report row created_at (ISO); used to detect in-flight refresh. */
  plaid_report_created_at?: string | null;
  plaid_institutions?: PlaidInstitution[];
  /**
   * True when final underwriting was forced past a stuck/failed Plaid flow, so
   * the statements (AI Analysis) decision stands in for the Plaid asset report.
   */
  plaid_manual_override?: boolean;
  /**
   * Where contract invites, signed copies and Plaid links are actually emailed.
   * Equals `email` for full accounts; the trial user's own address for trial orgs.
   */
  outbound_recipient_email?: string;
  /** True when `outbound_recipient_email` is the trial redirect rather than the merchant. */
  outbound_recipient_is_trial_override?: boolean;
}

export interface PlaidInstitution {
  id: number;
  institution_id: string;
  institution_name: string;
  accounts: Array<{ name?: string; mask?: string; subtype?: string; type?: string }>;
  status: "active" | "login_required" | "removed";
  connected_at: string | null;
  /** Plaid Auth (/auth/get) capture status for this institution. */
  auth_status?: "pending" | "ready" | "unsupported" | "failed";
  auth_error_code?: string;
}

export interface CreateBorrowerData {
  company_name: string;
  owner_name?: string;
  email?: string;
  phone?: string;
  phone_country_code?: string;
  phone_number?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  postal_code?: string;
  country?: string;
  business_address_line1?: string;
  business_city?: string;
  business_state?: string;
  business_postal_code?: string;
  ein?: string;
  entity_type?: EntityType | string;
  app_status?: AppStatus;
  organization_ids?: number[];
  merchants_dba?: string;
  owner_drivers_license?: string;
  /** YYYY-MM-DD. Empty clears the stored date. */
  owner_date_of_birth?: string;
  owner_ssn?: string;
  /** Create only: what the application PDF gave that the form has no input for. The create
   * writes them as the application's "[Extracted from Application PDF]" note. */
  extracted_industry?: string;
  extracted_requested_amount?: string;
}

export type UpdateBorrowerData = Partial<CreateBorrowerData> & {
  requires_plaid?: boolean;
};

export interface PaginatedResponse<T> {
  count: number;
  total_pages: number;
  current_page: number;
  page_size: number;
  next: string | null;
  previous: string | null;
  results: T[];
}
